import * as React from "react";
import { AnnotationEditorType, getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import { EventBus, PDFLinkService, PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";
import "../../utils/pdfWorker";
import { documentEditingService } from "../../services/documentEditingService";
import { BausteinMenue } from "./pdf-editor/BausteinMenue";
import { bausteinAlsPng } from "./pdf-editor/bausteinBild";
import { PlatzierEbene, type PlatzierOrt } from "./pdf-editor/PlatzierEbene";
import { BREITE_PT, platzierHinweis as platzierHinweisText, type PlatzierAuftrag } from "./pdf-editor/platzieren";
import type { PdfBaustein } from "../../services/pdfBausteinService";
import { SeitenOrganisator } from "./pdf-editor/SeitenOrganisator";
import { HAUPT, type Drehung, type SeitenEintrag } from "./pdf-editor/seitenOps";
import { EntfernenEbene } from "./pdf-editor/EntfernenEbene";
import { StreichEbene } from "./pdf-editor/StreichEbene";
import { streichungenSchreiben } from "./pdf-editor/streichungSchreiben";
import { AusschnittEbene } from "./pdf-editor/AusschnittEbene";
import { nimmAuf } from "./pdf-editor/ausschnittAufnehmen";
import { stempelSetzen, uiManagerVon } from "./pdf-editor/stempelSetzen";
import { RueckgaengigStapel, tastenkuerzel, type RueckgaengigEintrag } from "./pdf-editor/rueckgaengig";
import { TextEbene } from "./pdf-editor/TextEbene";
import { ErkannterTextFeld } from "./pdf-editor/ErkannterTextFeld";
import { SchnellKnopf, WerkzeugGruppe, WerkzeugKnopf, Werkzeugleiste, type ReiterDefinition } from "./pdf-editor/Werkzeugleiste";
import { PdfIcons } from "./pdf-editor/pdfIcons";
import { SpeichernPopup, type BildZiel, type FassungZiel } from "./pdf-editor/SpeichernPopup";
import { aenderungsText as beschreibeAenderung, zaehleAnmerkungen } from "./pdf-editor/aenderungsText";
import { nameFuerZiel } from "./pdf-editor/bildFormat";
import type { BildSitzung } from "./pdf-editor/bildSitzung";
import "./pdf-editor/pdfEditor.css";
import { redaktionsBand, schneidet } from "./pdf-editor/zeilen";
import {
    alsAuftrag,
    anzahlAusschnitte,
    anzahlBereiche,
    anzahlGeschwaerzt,
    anzahlStreichungen,
    ausschnittBilder,
    bearbeitungReducer,
    verschobeneAusschnitte,
    istLeer,
    LEERER_STAND,
    STREICH_FARBEN,
    type BearbeitungAktion,
    type PdfRect,
    type StreichFarbe,
} from "./pdf-editor/pdfBearbeitung";
import { KommentarEbene } from "./pdf-editor/KommentarEbene";
import { KommentareOhneStelle } from "./pdf-editor/KommentarKnoepfe";
import { kommentarZuordnung, verketteZuordnung } from "./pdf-editor/kommentarZuordnung";
import { pdfJsGeometrie } from "./pdf-editor/seitenGeometrie";
import type { KommentarSteuerung } from "./pdf-editor/useDokumentKommentare";
import { bestaetigen } from "../redesign/bestaetigen";
import { alleDokumente, alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * PDF-Editor auf dem vollen PDF.js-Viewer.
 *
 * WARUM NICHT `react-pdf` wie im Lese-Viewer: `react-pdf` stellt den
 * `AnnotationEditorLayer` nicht bereit — der gehört zum Viewer-Paket
 * (`pdfjs-dist/web/pdf_viewer`), nicht zur Core-API. Genau dieser Layer bringt
 * Freitext, Zeichnen, Hervorheben und Stempel (Bilder) fertig mit; ihn über
 * react-pdf nachzubauen hieße, Textauswahl, Koordinatentransformation,
 * Zoom-Verhalten und Undo/Redo selbst zu schreiben.
 *
 * Der Preis ist dieser imperative Block: PDF.js verwaltet das DOM selbst. Die
 * Kapselung endet an dieser Komponente — das Ansehen bleibt bei `react-pdf`.
 *
 * BILDER (User 04.10.2026) laufen durch denselben Editor: `url` ist dann die
 * PDF-Hülle des Bildes, `bild` die Sitzung dazu (`pdf-editor/bildSitzung.ts`).
 * Was auf einem Bild nicht geht — Textzeilen ändern, Seiten umbauen —, blendet
 * die Leiste aus. Beim Speichern wählt der Nutzer: als Bild (die Seite wird im
 * Browser in Originalauflösung gerastert) oder als PDF (der bisherige Weg, das
 * Dokument wird umgewandelt). Beides legt wie gewohnt eine Fassung an.
 */


// Das frühere Werkzeug „Unterschrift“ (`AnnotationEditorType.SIGNATURE`) tat nichts — der
// Viewer hat keinen `signatureManager`. Unterschriften und Stempel kommen jetzt aus den
// Einstellungen und werden über `BausteinMenue` als PDF.js-Stempel gesetzt.
type ToolKey =
    | "none"
    | "freetext"
    | "ink"
    | "highlight"
    | "entfernen"
    | "ausschneiden"
    | "text"
    | "kommentar"
    | "streichen"
    | "schwaerzen";

const TOOL_MODE: Record<ToolKey, number> = {
    none: AnnotationEditorType.NONE,
    freetext: AnnotationEditorType.FREETEXT,
    ink: AnnotationEditorType.INK,
    highlight: AnnotationEditorType.HIGHLIGHT,
    // Entfernen braucht keinen PDF.js-Editor — die Textauswahl muss frei bleiben.
    entfernen: AnnotationEditorType.NONE,
    // Ausschneiden ebenso — Rechteck aufziehen oder Text auswählen + Strg+X.
    ausschneiden: AnnotationEditorType.NONE,
    // Text bearbeiten ebenso — die Text-Ebene legt ihren eigenen Kasten auf die Seite.
    text: AnnotationEditorType.NONE,
    // Kommentare liegen in KMVS, nicht im PDF — PDF.js bleibt außen vor.
    kommentar: AnnotationEditorType.NONE,
    // T-363: Durchstreichen und Schwärzen sind eigene Ebenen — die Textauswahl muss frei bleiben.
    streichen: AnnotationEditorType.NONE,
    schwaerzen: AnnotationEditorType.NONE,
};

/** Erstes Bild aus den Daten eines `paste`-Ereignisses — sonst `null`. */
function bildAusZwischenablage(daten: DataTransfer | null): File | null {
    if (!daten) return null;
    for (const item of Array.from(daten.items ?? [])) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
            const datei = item.getAsFile();
            if (datei) return datei;
        }
    }
    return Array.from(daten.files ?? []).find((f) => f.type.startsWith("image/")) ?? null;
}

export interface EditorSteuerung {
    /** Öffnet das Speichern-Popup. `true` = gespeichert, `false` = abgebrochen oder gescheitert. */
    speichern: () => Promise<boolean>;
}

export const PdfEditorViewer: React.FC<{
    docId: number;
    /** Object-URL des aktuellen Dokuments. */
    url: string;
    /** Aktueller Dateiname — Vorbelegung im Speichern-Dialog. */
    fileName: string;
    /**
     * Nach erfolgreichem Speichern — der Aufrufer lädt neu und verlässt den Editor.
     * `neuesDokument` ist gesetzt, wenn „als eigenes Dokument“ gespeichert wurde.
     */
    onSaved: (ergebnis?: { neuesDokument?: { id: number; fileName: string } }) => void;
    /**
     * Bearbeitet wird eine FRÜHERE Fassung (User 07.10.2026): `url` zeigt auf
     * deren Datei. Beim Speichern fragt das Popup, ob daraus die neueste
     * Fassung wird oder ein eigenes Dokument.
     */
    fassung?: { versionNo: number } | null;
    onCancel: () => void;
    /** Kommentare des Dokuments (in KMVS, nicht im PDF) — ohne: keine Kommentar-Ebene. */
    kommentare?: KommentarSteuerung;
    /** Das Dokument ist ein Bild — `url` zeigt auf seine PDF-Hülle. */
    bild?: BildSitzung | null;
    /**
     * Der Editor ist eingehängt, aber gerade nicht zu sehen (anderer Dokument-Tab
     * vorn, `PdfDokumentPage`). Dann hört er auf keine Taste und kein Einfügen —
     * Strg+Z, Strg+V und Entf gehören dem sichtbaren Tab.
     */
    sichtbar?: boolean;
    /** Meldet, ob es ungespeicherte Änderungen gibt (Punkt am Dokument-Tab). */
    onAenderungen?: (ungespeichert: boolean) => void;
    /** Von außen steuerbar: `speichern()` öffnet das Speichern-Popup; `true`, sobald gespeichert ist. */
    steuerung?: React.MutableRefObject<EditorSteuerung | null>;
}> = ({ docId, url, fileName, onSaved, onCancel, kommentare, bild = null, sichtbar = true, onAenderungen, steuerung, fassung = null }) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const viewerRef = React.useRef<PDFViewer | null>(null);
    const pdfRef = React.useRef<PDFDocumentProxy | null>(null);

    const [ready, setReady] = React.useState(false);
    // „Seitenbreite“ steht noch aus, weil das Dokument unsichtbar geladen hat.
    const breiteOffen = React.useRef(false);
    const warVersteckt = React.useRef(false);
    const [tool, setTool] = React.useState<ToolKey>("none");
    // Reiter der Leiste (User 02.10.2026, wie bei Microsoft). Das aktive
    // Werkzeug bleibt beim Reiterwechsel aktiv.
    const [reiter, setReiter] = React.useState("start");
    // T-216: Seitenfeld mit dem erkannten Text (auch bei Scans) zum Kopieren.
    const [textFeldOffen, setTextFeldOffen] = React.useState(false);
    // EIN Rückgängig-Stapel für alles (Plan 02.10.2026, PR 3): PDF.js-Schritte
    // (Text hinzufügen, Zeichnen, Markieren, Stempel) und Schnappschüsse der
    // Bearbeitung von außen (Entfernen, Ausschneiden, Text bearbeiten, Drehen)
    // in einer Reihenfolge. `stapelTick` rendert die Knöpfe nach jeder Änderung.
    const stapel = React.useRef(new RueckgaengigStapel());
    const [, setStapelTick] = React.useState(0);
    const stapelGeaendert = React.useCallback(() => setStapelTick((n) => n + 1), []);
    const kannRueckgaengig = stapel.current.kannRueckgaengig();
    const kannWiederholen = stapel.current.kannWiederholen();
    const [saving, setSaving] = React.useState(false);
    const [dirty, setDirty] = React.useState(false);
    // Grad im Uhrzeigersinn, die beim Speichern ins PDF wandern. PDF.js dreht
    // hier nur die Ansicht — geschrieben wird die Drehung serverseitig.
    const [drehung, setDrehung] = React.useState(0);
    const [error, setError] = React.useState<string | null>(null);
    // Markierte Bereiche, die der Server beim Speichern ECHT entfernt (PO 28.09.2026).
    const [bearbeitung, bearbeitungAendern] = React.useReducer(bearbeitungReducer, LEERER_STAND);
    // Aktueller Stand synchron (für Schnappschüsse, auch bei zwei Aktionen im selben Tick).
    const bearbeitungRef = React.useRef(bearbeitung);
    /**
     * Jede Aktion der Ebenen läuft hier durch: Was den Stand ändert, kommt als
     * Schnappschuss auf den Rückgängig-Stapel — außer dem Nachtragen eines
     * Ausschnitt-Bildes (kein eigener Schritt), dem Seitenumbau (nicht
     * rücknehmbar, Stapel beginnt neu) und dem Zurücksetzen selbst.
     */
    const aendern = React.useCallback(
        (a: BearbeitungAktion) => {
            const vorher = bearbeitungRef.current;
            const nachher = bearbeitungReducer(vorher, a);
            if (nachher !== vorher && a.typ !== "ausschnittBild" && a.typ !== "seitenumbau" && a.typ !== "leeren" && a.typ !== "ersetzen") {
                stapel.current.merke({ art: "stand", vorher });
                stapelGeaendert();
            }
            bearbeitungRef.current = nachher;
            bearbeitungAendern(a);
        },
        [stapelGeaendert],
    );
    const [hinweis, setHinweis] = React.useState<string | null>(null);
    // Laufendes Platzieren (Unterschrift, Stempel, Bild aus der Zwischenablage; PR 4).
    // Der Auftrag lebt hier, nicht im Menü: ein Reiterwechsel beendet ihn nicht, die
    // Vorschau hängt am Mauszeiger (`PlatzierEbene`), der Seitenklick setzt.
    const [platzieren, setPlatzieren] = React.useState<PlatzierAuftrag | null>(null);
    const platzierHinweis = platzieren ? platzierHinweisText(platzieren.art) : null;
    const anzahlEntfernen = anzahlBereiche(bearbeitung);
    const anzahlZeilen = bearbeitung.ersetzen.length;
    const anzahlVerschoben = anzahlAusschnitte(bearbeitung);
    // T-363: geschwärzte und durchgestrichene Stellen.
    const geschwaerzt = anzahlGeschwaerzt(bearbeitung);
    const gestrichen = anzahlStreichungen(bearbeitung);
    // Strichfarbe fürs Durchstreichen — Rot wie auf Papier, wählbar Schwarz/Blau.
    const [streichFarbe, setStreichFarbe] = React.useState<StreichFarbe>("rot");
    // Entfernen gewinnt über eine geänderte Zeile (der Server schreibt deren
    // neuen Text dann nicht) — das soll man beim Markieren erfahren, nicht erst
    // nach dem Speichern.
    const bekannteMarkierungen = React.useRef(new Set<string>());
    React.useEffect(() => {
        const neu = bearbeitung.entfernen.filter((m) => !bekannteMarkierungen.current.has(m.id));
        bekannteMarkierungen.current = new Set(bearbeitung.entfernen.map((m) => m.id));
        if (neu.length === 0) return;
        const getroffen = bearbeitung.ersetzen.filter((e) =>
            neu.some((m) => m.seite === e.seite && schneidet(m.rect, redaktionsBand(e))),
        );
        if (getroffen.length === 1) {
            setHinweis(`Die geänderte Zeile „${getroffen[0].text}“ liegt im entfernten Bereich und entfällt.`);
        } else if (getroffen.length > 1) {
            setHinweis(`${getroffen.length} geänderte Zeilen liegen im entfernten Bereich und entfallen.`);
        } else {
            // Ausschnitte werden NACH dem Entfernen gezeichnet — sie bleiben sichtbar.
            const echte = neu.filter((m) => m.quelle !== "ausschnitt");
            const ueberZiel = verschobeneAusschnitte(bearbeitung).some((a) =>
                echte.some((m) => m.seite === a.seite && schneidet(m.rect, a.rect)),
            );
            if (ueberZiel) setHinweis("Der verschobene Ausschnitt liegt über dem entfernten Bereich und bleibt sichtbar.");
        }
        // Läuft bei jeder Änderung, meldet aber nur zu NEUEN Markierungen (sonst früher Ausstieg).
    }, [bearbeitung]);
    // PO 30.08.2026: Beim Speichern lässt sich ein Kommentar hinterlegen und
    // zugleich umbenennen. Der Dialog steht VOR dem Speichern, nicht danach:
    // hinterher wüsste niemand mehr, was er gerade geändert hat.
    const [dialogOffen, setDialogOffen] = React.useState(false);
    const [kommentar, setKommentar] = React.useState("");
    const [name, setName] = React.useState(fileName);
    // Bild: als Bild speichern (Standard) oder zum PDF machen. Der Name im
    // Popup zeigt die Endung, die das Dokument danach trägt.
    const [bildZiel, setBildZiel] = React.useState<BildZiel>("bild");
    React.useEffect(
        () => setName(bild ? nameFuerZiel(fileName, fileName, bild.ziel, "bild") : fileName),
        [bild, fileName],
    );
    // Frühere Fassung: neueste Fassung des Dokuments oder eigenes Dokument daneben.
    const [fassungZiel, setFassungZiel] = React.useState<FassungZiel>("neueste");
    const fassungZielWaehlen = React.useCallback(
        (z: FassungZiel) => {
            setFassungZiel(z);
            if (!fassung) return;
            // Ein eigenes Dokument braucht einen eigenen Namen — sonst stünden
            // zwei gleichnamige Dateien nebeneinander. Nur den Vorschlag
            // tauschen; was der Nutzer selbst getippt hat, bleibt.
            const zusatz = ` (Fassung ${fassung.versionNo})`;
            setName((n) => {
                const punkt = n.lastIndexOf(".");
                const stamm = punkt > 0 ? n.slice(0, punkt) : n;
                const endung = punkt > 0 ? n.slice(punkt) : "";
                if (z === "eigenes") return stamm.endsWith(zusatz) ? n : `${stamm}${zusatz}${endung}`;
                return stamm.endsWith(zusatz) ? `${stamm.slice(0, -zusatz.length)}${endung}` : n;
            });
        },
        [fassung],
    );
    const bildZielWaehlen = React.useCallback(
        (z: BildZiel) => {
            setBildZiel(z);
            if (bild) setName((n) => nameFuerZiel(n, fileName, bild.ziel, z));
        },
        [bild, fileName],
    );

    // Seiten-Organisator: „blatt“ = PDF.js-Viewer mit Werkzeugen, „seiten“ =
    // Kachelansicht. Übernommene Seiten lädt der Viewer aus einer eigenen
    // Object-URL neu (`arbeitsUrl`); die `url` des Aufrufers bleibt unangetastet.
    const [modus, setModus] = React.useState<"blatt" | "seiten">("blatt");
    const [arbeitsUrl, setArbeitsUrl] = React.useState(url);
    const eigeneUrlRef = React.useRef<string | null>(null);
    const [organisatorBytes, setOrganisatorBytes] = React.useState<Uint8Array | null>(null);
    const [seitenGeaendert, setSeitenGeaendert] = React.useState(false);
    const [seitenHinweis, setSeitenHinweis] = React.useState(false);
    // `saveDocument()` läuft — währenddessen weder erneut öffnen noch speichern.
    const [seitenLaden, setSeitenLaden] = React.useState(false);
    // Kommentare hängen an Seiten der GESPEICHERTEN Fassung. Nach „Seiten
    // übernehmen“: gespeicherte Seite i steht jetzt an Position zuordnung[i]
    // (-1 = gelöscht). Beim Speichern geht die Zuordnung an den Server.
    const [kommentarSeiten, setKommentarSeiten] = React.useState<number[] | null>(null);
    // Fassung gespeichert, aber die Zuordnung der Kommentare scheiterte: der
    // Editor bleibt offen, „erneut zuordnen“ schickt NUR die Zuordnung.
    const [zuordnungOffen, setZuordnungOffen] = React.useState(false);
    const [zuordnet, setZuordnet] = React.useState(false);
    const eigeneUrlFreigeben = React.useCallback(() => {
        if (eigeneUrlRef.current) URL.revokeObjectURL(eigeneUrlRef.current);
        eigeneUrlRef.current = null;
    }, []);
    React.useEffect(() => {
        eigeneUrlFreigeben();
        setArbeitsUrl(url);
        setSeitenGeaendert(false);
    }, [url, eigeneUrlFreigeben]);
    React.useEffect(() => eigeneUrlFreigeben, [eigeneUrlFreigeben]);

    // Aufbau des Viewers. Läuft genau einmal je Dokument — PDF.js verwaltet das
    // DOM darunter selbst, ein Re-Render darf ihn nicht neu erzeugen.
    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let cancelled = false;
        setReady(false);
        setTool("none");
        const eventBus = new EventBus();
        const linkService = new PDFLinkService({ eventBus });
        const viewer = new PDFViewer({
            container,
            eventBus,
            linkService,
            annotationEditorMode: AnnotationEditorType.NONE,
        });
        linkService.setViewer(viewer);
        viewerRef.current = viewer;

        // Sobald irgendein Editor etwas ändert, gibt es ungespeicherte Arbeit.
        // PDF.js meldet über dieses Ereignis aber auch eine bloße Textauswahl
        // (`hasSelectedText`, mit dem vollen Zustand). Maßgeblich ist allein,
        // ob es etwas rückgängig zu machen gibt — `isEmpty` taugt nicht, denn
        // ein PDF mit vorhandenen Anmerkungen ist nie „leer“, auch ungeändert.
        // Ohne diese Angabe (unvollständiges Ereignis) lieber als Änderung werten.
        const onEditorStates = (e?: { details?: { hasSomethingToUndo?: boolean } }) => {
            const d = e?.details;
            // Alles zurückgenommen → keine ungespeicherte PDF.js-Arbeit mehr.
            if (d && "hasSomethingToUndo" in d) {
                setDirty(!!d.hasSomethingToUndo);
                return;
            }
            setDirty(true);
        };
        eventBus.on("annotationeditorstateschanged", onEditorStates);

        const onPagesInit = () => {
            if (cancelled) return;
            // Unsichtbar (anderer Dokument-Tab vorn) hat der Container keine
            // Breite — „Seitenbreite“ wird dann beim Zeigen nachgeholt.
            if (container.clientWidth > 0) viewer.currentScaleValue = "page-width";
            else breiteOffen.current = true;
            // Jeden PDF.js-Befehl auf dem gemeinsamen Stapel mitzählen. Der
            // UI-Manager entsteht je geladenem Dokument neu — die Hülle auch.
            const ui = uiManagerVon(viewer) as (ReturnType<typeof uiManagerVon> & { addCommands?: (p: unknown) => void }) | null;
            if (ui && typeof ui.addCommands === "function" && !(ui as { __kmvsStapel?: boolean }).__kmvsStapel) {
                const original = ui.addCommands.bind(ui);
                ui.addCommands = (p: unknown) => {
                    original(p);
                    stapel.current.merke({ art: "pdfjs" });
                    stapelGeaendert();
                };
                (ui as { __kmvsStapel?: boolean }).__kmvsStapel = true;
            }
            setReady(true);
        };
        eventBus.on("pagesinit", onPagesInit);

        const task = getDocument({ url: arbeitsUrl, isEvalSupported: false });
        task.promise
            .then((pdf) => {
                if (cancelled) {
                    void pdf.destroy();
                    return;
                }
                pdfRef.current = pdf;
                viewer.setDocument(pdf);
                linkService.setDocument(pdf, null);
            })
            .catch(() => {
                if (!cancelled) setError(bild ? "Das Bild konnte nicht geöffnet werden." : "Das PDF konnte nicht geöffnet werden.");
            });

        return () => {
            cancelled = true;
            eventBus.off("annotationeditorstateschanged", onEditorStates);
            eventBus.off("pagesinit", onPagesInit);
            // Reihenfolge zählt: erst den Viewer lösen, dann das Dokument
            // schließen — umgekehrt greift der Viewer auf Zerstörtes zu.
            try {
                viewer.setDocument(null as unknown as PDFDocumentProxy);
            } catch {
                /* Viewer war nie vollständig initialisiert. */
            }
            void task.destroy();
            void pdfRef.current?.destroy();
            pdfRef.current = null;
            viewerRef.current = null;
        };
        // `stapelGeaendert` ist ein stabiler Callback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arbeitsUrl]);

    // Ein anderes Werkzeug, Drehen oder der Seiten-Organisator beenden ein laufendes
    // Platzieren — sonst setzte der nächste Seitenklick doch noch die Unterschrift.
    const platzierenAbbrechen = React.useCallback(() => setPlatzieren(null), []);
    // Die Object-URL der Vorschau lebt genau so lange wie ihr Auftrag.
    React.useEffect(() => {
        const url = platzieren?.url;
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [platzieren?.url]);

    const selectTool = React.useCallback((next: ToolKey) => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        platzierenAbbrechen();
        setSeitenHinweis(false);
        // Erneuter Klick auf dasselbe Werkzeug schaltet es ab.
        setHinweis(null);
        setTool((prev) => {
            const value = prev === next ? "none" : next;
            viewer.annotationEditorMode = { mode: TOOL_MODE[value] };
            return value;
        });
    }, [platzierenAbbrechen]);

    // Dokument-Tab verlassen: Werkzeug und Platzieren aus. PDF.js hört mit einem
    // aktiven Werkzeug am FENSTER auf Tasten (Entf, Strg+A …) und aufs Einfügen —
    // das träfe sonst diesen unsichtbaren Editor statt den sichtbaren.
    // Wieder da: Seiten neu einpassen (das Fenster kann inzwischen anders groß sein).
    React.useEffect(() => {
        const viewer = viewerRef.current;
        if (!sichtbar) {
            warVersteckt.current = true;
            platzierenAbbrechen();
            setTool("none");
            // Vor dem Laden des Dokuments kennt PDF.js noch keinen Editor (der Setter
            // wirft dann) — und es ist auch noch kein Werkzeug an.
            try {
                if (viewer && ready) viewer.annotationEditorMode = { mode: AnnotationEditorType.NONE };
            } catch {
                /* Dokument inzwischen entladen — nichts abzuschalten. */
            }
            return;
        }
        // Nur nach einem Verstecken — beim ersten Aufbau passt PDF.js selbst ein.
        if (!viewer || !ready || !warVersteckt.current) return;
        warVersteckt.current = false;
        if (breiteOffen.current || viewer.currentScaleValue === "page-width") {
            breiteOffen.current = false;
            viewer.currentScaleValue = "page-width";
        }
        viewer.update();
    }, [platzierenAbbrechen, ready, sichtbar]);

    // Ref als Getter, damit die Platzier-Ebene immer den aktuellen Viewer sieht.
    const viewerVon = React.useCallback(() => viewerRef.current, []);
    /** Platzieren beginnt: Werkzeug aus (das Einsetzen schaltet PDF.js ohnehin in den Stempel-Modus). */
    const platzierenStarten = React.useCallback((auftrag: PlatzierAuftrag) => {
        setTool("none");
        setHinweis(null);
        setSeitenHinweis(false);
        setPlatzieren(auftrag);
    }, []);
    // Unterschrift/Stempel aus dem Menü: das Bild entsteht jetzt (Datum im Bild = heute),
    // der Nutzer setzt es mit dem nächsten Seitenklick.
    const bausteinWaehlen = React.useCallback(
        async (b: PdfBaustein) => {
            try {
                const png = await bausteinAlsPng(b, new Date());
                platzierenStarten({ art: b.art, bild: png, url: URL.createObjectURL(png), breitePt: BREITE_PT[b.art] });
            } catch (err) {
                setError(err instanceof Error && err.message ? err.message : "Die Vorschau konnte nicht erzeugt werden.");
            }
        },
        [platzierenStarten],
    );
    const platzierBreite = React.useCallback((breitePt: number) => setPlatzieren((p) => (p ? { ...p, breitePt } : p)), []);
    /** Der Seitenklick löst den Auftrag ein — das Bild wird PDF.js-Stempel an der Stelle. */
    const platzierenSetzen = React.useCallback(
        (ort: PlatzierOrt) => {
            const a = platzieren;
            const viewer = viewerRef.current;
            if (!a || !viewer) return;
            setPlatzieren(null);
            stempelSetzen(viewer, a.bild, ort, a.breitePt)
                .then(() => setDirty(true))
                .catch((err: unknown) =>
                    setError(err instanceof Error && err.message ? err.message : "Einsetzen fehlgeschlagen."),
                );
        },
        [platzieren],
    );

    // Strg+V mit einem Bild in der Zwischenablage (Screenshot, kopiertes Logo …):
    // startet das Platzieren — das Bild hängt am Mauszeiger, der Klick setzt es als
    // Stempel. Über das `paste`-Ereignis statt `navigator.clipboard.read()` — Edge
    // fragt dann nicht nach einer Berechtigung. Ohne Bild bleibt das Einfügen bei
    // PDF.js (kopierte Anmerkungen) bzw. beim Eingabefeld.
    React.useEffect(() => {
        if (!ready || modus !== "blatt" || !sichtbar) return;
        const onPaste = (e: ClipboardEvent) => {
            const ziel = e.target as HTMLElement | null;
            const gesperrt =
                "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role=dialog]";
            if (ziel && typeof ziel.closest === "function" && ziel.closest(gesperrt)) return;
            const bild = bildAusZwischenablage(e.clipboardData);
            if (!bild || !viewerRef.current) return;
            // Nicht zusätzlich von PDF.js einfügen lassen.
            e.preventDefault();
            e.stopImmediatePropagation();
            platzierenStarten({ art: "bild", bild, url: URL.createObjectURL(bild), breitePt: BREITE_PT.bild });
        };
        alleDokumente.addEventListener("paste", onPaste, true);
        return () => alleDokumente.removeEventListener("paste", onPaste, true);
    }, [modus, platzierenStarten, ready, sichtbar]);

    // Seiten-Geometrie für die Kommentar-Ebene — neu je geladenem Dokument.
    const kommentarGeometrie = React.useMemo(
        () => (ready && viewerRef.current ? pdfJsGeometrie(viewerRef.current) : null),
        // `arbeitsUrl`: nach „Seiten übernehmen“ ist es ein neuer Viewer-Stand.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ready, arbeitsUrl],
    );
    const kommentareZeigen = kommentare?.setSichtbar;
    const kommentarEntwurf = React.useCallback(() => {
        setTool("none");
        kommentareZeigen?.(true);
    }, [kommentareZeigen]);

    const zoom = React.useCallback((factor: number) => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        viewer.currentScale = Math.min(4, Math.max(0.25, viewer.currentScale * factor));
    }, []);
    const zoomAnpassen = React.useCallback(() => {
        const viewer = viewerRef.current;
        if (viewer) viewer.currentScaleValue = "page-width";
    }, []);

    // Rückgängig/Wiederholen über alle Werkzeuge. Ein PDF.js-Eintrag geht an
    // dessen Befehlsstapel; ein Schnappschuss ersetzt den Stand der Bearbeitung
    // (ohne neuen Eintrag, `ersetzen` zählt nicht); eine Drehung stellt die Ansicht um.
    const drehungRef = React.useRef(drehung);
    const stapelEintragAnwenden = React.useCallback((e: RueckgaengigEintrag, richtung: "undo" | "redo") => {
        const viewer = viewerRef.current;
        if (e.art === "pdfjs") {
            const ui = viewer ? uiManagerVon(viewer) : null;
            if (richtung === "undo") ui?.undo?.();
            else ui?.redo?.();
            return;
        }
        if (e.art === "stand") {
            const ziel = richtung === "undo" ? e.vorher : e.nachher;
            if (!ziel) return;
            bearbeitungRef.current = ziel;
            bearbeitungAendern({ typ: "ersetzen", stand: ziel });
            return;
        }
        const grad = richtung === "undo" ? e.vorher : e.nachher;
        if (grad === undefined) return;
        drehungRef.current = grad;
        setDrehung(grad);
        if (viewer) viewer.pagesRotation = grad;
    }, []);
    const rueckgaengig = React.useCallback(() => {
        const e = stapel.current.rueckgaengig({ stand: bearbeitungRef.current, drehung: drehungRef.current });
        if (!e) return;
        stapelEintragAnwenden(e, "undo");
        stapelGeaendert();
    }, [stapelEintragAnwenden, stapelGeaendert]);
    const wiederholen = React.useCallback(() => {
        const e = stapel.current.wiederholen();
        if (!e) return;
        stapelEintragAnwenden(e, "redo");
        stapelGeaendert();
    }, [stapelEintragAnwenden, stapelGeaendert]);

    // Strg+Z / Strg+Y VOR PDF.js abfangen (Capture am Fenster): PDF.js kennt die
    // Tasten nur in seinen eigenen Modi und wüsste nichts von den Schnappschüssen.
    // In Eingabefeldern und im Freitext-Editor gilt deren eigenes Rückgängig.
    React.useEffect(() => {
        if (!ready || modus !== "blatt" || !sichtbar) return;
        const onKey = (e: KeyboardEvent) => {
            if (dialogOffen || saving) return;
            const was = tastenkuerzel(e);
            if (!was) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (was === "rueckgaengig") rueckgaengig();
            else wiederholen();
        };
        alleFenster.addEventListener("keydown", onKey, true);
        return () => alleFenster.removeEventListener("keydown", onKey, true);
    }, [dialogOffen, modus, ready, rueckgaengig, saving, sichtbar, wiederholen]);

    /**
     * Quer eingescannte Seiten geraderücken: je Klick 90 Grad im Uhrzeigersinn,
     * nach vier Klicks steht wieder alles wie zu Beginn (0 = nichts zu tun).
     * `pagesRotation` dreht ALLE Seiten — ein gescanntes Bündel liegt immer
     * gleich herum.
     */
    const drehen = React.useCallback(() => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        platzierenAbbrechen();
        setSeitenHinweis(false);
        const vorher = drehungRef.current;
        const next = (vorher + 90) % 360;
        stapel.current.merke({ art: "drehung", vorher });
        stapelGeaendert();
        drehungRef.current = next;
        viewer.pagesRotation = next;
        setDrehung(next);
    }, [platzierenAbbrechen, stapelGeaendert]);

    /**
     * In die Seitenansicht wechseln. Vorher den Arbeitsstand ziehen
     * (`saveDocument()`), damit schon gesetzte Annotationen mitwandern.
     */
    const seitenOeffnen = React.useCallback(async () => {
        const pdf = pdfRef.current;
        const viewer = viewerRef.current;
        if (!pdf || !viewer || seitenLaden) return;
        setSeitenLaden(true);
        viewer.annotationEditorMode = { mode: AnnotationEditorType.NONE };
        setTool("none");
        platzierenAbbrechen();
        setError(null);
        setSeitenHinweis(false);
        try {
            setOrganisatorBytes(await pdf.saveDocument());
            setModus("seiten");
        } catch {
            setError("Die Seiten konnten nicht geöffnet werden.");
        } finally {
            setSeitenLaden(false);
        }
    }, [platzierenAbbrechen, seitenLaden]);

    /**
     * Neue Seitenfolge übernehmen: der Viewer lädt aus den neuen Bytes neu.
     * PDF.js liest die Annotationen darin als bestehende ein — Freitext,
     * Zeichnung und Markierung bleiben bearbeitbar, der Rückgängig-Verlauf
     * von PDF.js beginnt aber neu. Die offene Drehung steckt jetzt in den Seiten.
     */
    const seitenUebernehmen = React.useCallback(
        ({ bytes, eintraege }: { bytes: Uint8Array; eintraege: SeitenEintrag[] }) => {
            // Markierungen zum Entfernen wandern mit ihrer Seite; die gelöschter
            // Seiten entfallen (Userspace-Rechtecke bleiben auch bei Drehung gültig).
            aendern({ typ: "seitenumbau", eintraege, haupt: HAUPT });
            // Der Viewer lädt neu — Rückgängig gilt ab hier neu (wie der PDF.js-Stapel).
            stapel.current.leeren();
            stapelGeaendert();
            drehungRef.current = 0;
            // Kommentare wandern mit — mehrere Umbauten vor dem Speichern verketten.
            const alteSeitenzahl = pdfRef.current?.numPages ?? 0;
            setKommentarSeiten((bisher) => verketteZuordnung(bisher, kommentarZuordnung(eintraege, alteSeitenzahl, HAUPT)));
            eigeneUrlFreigeben();
            const neu = URL.createObjectURL(new Blob([bytes.slice()], { type: "application/pdf" }));
            eigeneUrlRef.current = neu;
            setArbeitsUrl(neu);
            setDrehung(0);
            setSeitenGeaendert(true);
            setSeitenHinweis(true);
            setOrganisatorBytes(null);
            setModus("blatt");
        },
        [aendern, eigeneUrlFreigeben, stapelGeaendert],
    );

    /** Ausschnitt aufnehmen — mit der PDF.js-Seite des aktuell geladenen Dokuments. */
    const ausschnittAufnehmen = React.useCallback(
        async (seite: number, rect: PdfRect, loeschen: PdfRect[]): Promise<Blob> => {
            const pdf = pdfRef.current;
            if (!pdf) throw new Error("Das PDF ist noch nicht geladen.");
            const page = await pdf.getPage(seite + 1);
            if (!bild) return nimmAuf(page, rect, loeschen);
            // Bild: so fein aufnehmen, wie das Original ist — sonst wäre der
            // verschobene Ausschnitt unschärfer als der Rest.
            const pxJePt = bild.breitePx / page.getViewport({ scale: 1, rotation: 0 }).width;
            return nimmAuf(page, rect, loeschen, Math.max(3, pxJePt));
        },
        [bild],
    );

    const seitenAbbrechen = React.useCallback(() => {
        setOrganisatorBytes(null);
        setModus("blatt");
    }, []);

    // Speichern von außen (Dokument-Tab schließen → „Speichern“): das Popup geht
    // auf, der Aufrufer erfährt, ob danach wirklich gespeichert ist.
    const speicherWunsch = React.useRef<((ok: boolean) => void) | null>(null);
    const wunschErledigen = React.useCallback((ok: boolean) => {
        speicherWunsch.current?.(ok);
        speicherWunsch.current = null;
    }, []);
    React.useEffect(() => {
        // Popup zu und kein Speichern unterwegs → der Wunsch ist abgebrochen oder gescheitert.
        if (!dialogOffen && !saving) wunschErledigen(false);
    }, [dialogOffen, saving, wunschErledigen]);
    React.useEffect(() => {
        if (!steuerung) return;
        steuerung.current = {
            speichern: () =>
                new Promise<boolean>((fertig) => {
                    wunschErledigen(false);
                    speicherWunsch.current = fertig;
                    setDialogOffen(true);
                }),
        };
        return () => {
            steuerung.current = null;
            wunschErledigen(false);
        };
    }, [steuerung, wunschErledigen]);

    const save = React.useCallback(async () => {
        const pdf = pdfRef.current;
        if (!pdf || saving) return;
        // Ein Ausschnitt ohne Bild würde auf dem Server fehlen — lieber kurz warten lassen.
        const bilder = ausschnittBilder(bearbeitung);
        if (bilder.some((b) => !b)) {
            setError("Ein Ausschnitt wird noch aufgenommen — bitte einen Moment warten und erneut speichern.");
            setDialogOffen(false);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // `saveDocument()` schreibt echte PDF-Annotationsobjekte in die Datei —
            // kein aufgemaltes Bild, sondern im PDF verankerte Inhalte.
            // Durchstreichungen (T-363) schreibt der Editor selbst als `/StrikeOut`
            // dazu — VOR der Verzweigung, damit sie als Bild wie als PDF gelten.
            const bytes = await streichungenSchreiben(await pdf.saveDocument(), bearbeitung.streichen);
            const notiz = kommentar.trim();
            const zielName = name.trim();
            const auftrag = alsAuftrag(bearbeitung);
            const neuerName = zielName && zielName !== fileName ? zielName : undefined;
            const alsEigenes = !!fassung && fassungZiel === "eigenes";
            const angaben = {
                fileName: neuerName,
                // „Zeige auch die Bearbeitung genau an“ (User 07.10.2026).
                aenderung:
                    beschreibeAenderung({
                        ausFassung: fassung?.versionNo,
                        bildZuPdf: !!bild && bildZiel === "pdf",
                        drehung,
                        seitenGeaendert,
                        entfernt: anzahlEntfernen,
                        geschwaerzt,
                        durchgestrichen: gestrichen,
                        zeilen: auftrag.ersetzen.length,
                        verschoben: auftrag.einfuegen.length,
                        ...zaehleAnmerkungen(pdf.annotationStorage),
                        sonstGeaendert: dirty,
                    }) || undefined,
                basisFassung: fassung?.versionNo,
                alsNeuesDokument: alsEigenes || undefined,
            };
            let gespeichert: { documentId: number; neuesDokument?: boolean; fileName?: string };
            if (bild && bildZiel === "bild") {
                // Das Bild bleibt ein Bild: die Seite samt allen Änderungen wird
                // hier gerastert — der Server bekommt fertige Pixel.
                const { pdfAlsBild } = await import("./pdf-editor/bildSitzung");
                const gerastert = await pdfAlsBild({
                    sitzung: bild,
                    pdfBytes: bytes,
                    auftrag,
                    bilder: bilder as Blob[],
                    drehung,
                });
                gespeichert = await documentEditingService.saveImageVersion(docId, gerastert, {
                    ...angaben,
                    note: notiz || undefined,
                });
            } else {
                gespeichert = await documentEditingService.saveVersion(docId, bytes, {
                    ...angaben,
                    // Nur der Kommentar des Nutzers. Was tatsächlich passiert
                    // ist („um 90° gedreht“), steht seit 07.10.2026 eigens in
                    // `aenderung` — vorher sprang es als Ersatz in den Kommentar.
                    note: notiz || undefined,
                    rotate: drehung || undefined,
                    bearbeitung: istLeer(auftrag) ? undefined : auftrag,
                    bilder: bilder.length ? (bilder as Blob[]) : undefined,
                    ausBild: bild ? true : undefined,
                });
            }
            setDirty(false);
            setDrehung(0);
            drehungRef.current = 0;
            setSeitenGeaendert(false);
            aendern({ typ: "leeren" });
            stapel.current.leeren();
            stapelGeaendert();
            setDialogOffen(false);
            // Als eigenes Dokument gespeichert: das bisherige ist unberührt,
            // seine Kommentare bleiben, wo sie sind.
            if (gespeichert?.neuesDokument) {
                setKommentarSeiten(null);
                wunschErledigen(true);
                onSaved({ neuesDokument: { id: gespeichert.documentId, fileName: gespeichert.fileName ?? zielName } });
                return;
            }
            // Die neue Fassung steht — jetzt die Kommentare ihren neuen Seiten
            // zuordnen. Scheitert das, bleibt der Editor offen und die Zuordnung
            // erhalten; sonst stünden die Kommentare dauerhaft an falschen Seiten.
            if (kommentare && kommentarSeiten && !(await kommentare.seitenZuordnen(kommentarSeiten))) {
                setZuordnungOffen(true);
                return;
            }
            setKommentarSeiten(null);
            wunschErledigen(true);
            onSaved();
        } catch (err) {
            // Der Server sagt genauer, was schiefging (z. B. „Das PDF konnte
            // nicht gedreht werden.“) — das gehört vor die Augen des Nutzers.
            const grund = err instanceof Error && err.message ? ` ${err.message}` : "";
            setError(`Speichern fehlgeschlagen.${grund} Die Änderungen sind noch da — bitte erneut versuchen.`);
            setDialogOffen(false);
        } finally {
            setSaving(false);
        }
    }, [aendern, anzahlEntfernen, bearbeitung, bild, bildZiel, dirty, docId, drehung, fassung, fassungZiel, fileName, geschwaerzt, gestrichen, kommentar, kommentarSeiten, kommentare, name, onSaved, saving, seitenGeaendert, stapelGeaendert, wunschErledigen]);

    // Eine Drehung ist ebenso ungespeicherte Arbeit wie ein eingefügter Text.
    const hatAenderungen =
        dirty ||
        drehung !== 0 ||
        seitenGeaendert ||
        anzahlEntfernen > 0 ||
        anzahlZeilen > 0 ||
        anzahlVerschoben > 0 ||
        geschwaerzt > 0 ||
        gestrichen > 0;
    // Nach außen melden (Punkt am Dokument-Tab); beim Verlassen des Editors ist nichts mehr offen.
    const onAenderungenRef = React.useRef(onAenderungen);
    onAenderungenRef.current = onAenderungen;
    React.useEffect(() => {
        onAenderungenRef.current?.(hatAenderungen || zuordnungOffen);
    }, [hatAenderungen, zuordnungOffen]);
    React.useEffect(() => () => onAenderungenRef.current?.(false), []);
    const aenderungsText = [
        drehung ? `um ${drehung}° gedreht` : null,
        anzahlEntfernen
            ? `${anzahlEntfernen} ${anzahlEntfernen === 1 ? "Bereich wird" : "Bereiche werden"} beim Speichern entfernt`
            : null,
        geschwaerzt
            ? `${geschwaerzt} ${geschwaerzt === 1 ? "Stelle wird" : "Stellen werden"} beim Speichern geschwärzt`
            : null,
        gestrichen ? `${gestrichen} ${gestrichen === 1 ? "Stelle" : "Stellen"} durchgestrichen` : null,
        anzahlZeilen ? `${anzahlZeilen} ${anzahlZeilen === 1 ? "Zeile" : "Zeilen"} geändert` : null,
        anzahlVerschoben
            ? `${anzahlVerschoben} ${anzahlVerschoben === 1 ? "Ausschnitt wird" : "Ausschnitte werden"} verschoben`
            : null,
    ]
        .filter(Boolean)
        .join(" · ");

    const zuordnungWiederholen = React.useCallback(async () => {
        if (!kommentare || !kommentarSeiten || zuordnet) return;
        setZuordnet(true);
        try {
            if (!(await kommentare.seitenZuordnen(kommentarSeiten))) return;
            setZuordnungOffen(false);
            setKommentarSeiten(null);
            onSaved();
        } finally {
            setZuordnet(false);
        }
    }, [kommentarSeiten, kommentare, onSaved, zuordnet]);

    const cancel = React.useCallback(async () => {
        if (
            hatAenderungen &&
            !(await bestaetigen({
                titel: "Änderungen verwerfen?",
                text: bild
                    ? "Deine Änderungen an diesem Bild sind noch nicht gespeichert."
                    : "Deine Änderungen an diesem PDF sind noch nicht gespeichert.",
                ja: "Verwerfen",
                nein: "Weiter bearbeiten",
                ton: "danger",
            }))
        )
            return;
        // Die neue Fassung ist schon gespeichert — der Aufrufer muss sie laden,
        // sonst zeigte er weiter die alte.
        if (zuordnungOffen) {
            const ok = await bestaetigen({
                titel: "Trotzdem schließen?",
                text: "Die neue Fassung ist gespeichert, aber die Kommentare stehen noch an den alten Seiten.",
                ja: "Schließen",
                nein: "Zurück",
            });
            if (!ok) return;
            onSaved();
            return;
        }
        onCancel();
    }, [bild, hatAenderungen, onCancel, onSaved, zuordnungOffen]);

    // Eine Zeile unter der Leiste, genau eine Meldung — Vorrang von oben nach unten.
    const hinweisZeile = error ? (
        <span className="pdfx-fehler" role="alert">{error}</span>
    ) : zuordnungOffen ? (
        <span className="pdfx-fehler" role="alert">
            Fassung gespeichert, aber die Kommentare konnten den neuen Seiten nicht zugeordnet werden.{" "}
            <button type="button" className="pdfx-kbtn" disabled={zuordnet} onClick={() => void zuordnungWiederholen()}>
                Kommentare erneut zuordnen
            </button>
        </span>
    ) : kommentare?.fehler ? (
        <span className="pdfx-fehler" role="alert">{kommentare.fehler}</span>
    ) : platzierHinweis ? (
        <span className="pdfx-platzieren" role="status">{platzierHinweis}</span>
    ) : hinweis ? (
        hinweis
    ) : seitenHinweis ? (
        "Seiten übernommen — Rückgängig gilt ab hier neu."
    ) : hatAenderungen ? (
        aenderungsText ? `Ungespeicherte Änderungen · ${aenderungsText}` : "Ungespeicherte Änderungen"
    ) : tool === "entfernen" ? (
        bild
            ? "Einen Bereich aufziehen — er wird beim Speichern weiß"
            : "Text auswählen und Entf drücken oder einen Bereich aufziehen — Strg+X schneidet aus"
    ) : tool === "schwaerzen" ? (
        bild
            ? "Einen Bereich aufziehen — er wird beim Speichern schwarz"
            : "Text auswählen und Entf drücken oder einen Bereich aufziehen — wird beim Speichern echt entfernt und geschwärzt"
    ) : tool === "streichen" ? (
        bild
            ? "Linie über die Stelle ziehen — sie wird durchgestrichen"
            : "Text auswählen — er wird durchgestrichen und bleibt lesbar. Ohne Text (Scan) oder mit Alt: Linie ziehen"
    ) : tool === "ausschneiden" ? (
        bild
            ? "Bereich aufziehen, dann Ausschneiden oder Entfernen wählen"
            : "Bereich aufziehen (über Text mit Alt), dann Ausschneiden oder Entfernen wählen — Text markieren und Strg+X schneidet direkt aus"
    ) : tool === "text" ? (
        "Auf eine Textzeile klicken und direkt lostippen — Enter übernimmt, Escape verwirft"
    ) : tool === "kommentar" ? (
        "Text auswählen, einen Bereich aufziehen oder auf eine Stelle klicken — der Kommentar bleibt in KMVS, nicht im PDF"
    ) : null;

    // Die Reiter (User 02.10.2026): Start | Einfügen | Zeichnen | Bearbeiten | Seiten | Ansicht.
    const reiterListe: ReiterDefinition[] = [
        {
            key: "start",
            label: "Start",
            inhalt: (
                <>
                    <WerkzeugGruppe titel="Text">
                        <WerkzeugKnopf
                            aktiv={tool === "freetext"}
                            icon={PdfIcons.textHinzu}
                            onClick={() => selectTool("freetext")}
                            label="Text hinzufügen"
                            title="Neuen Text an eine freie Stelle setzen"
                        />
                        <WerkzeugKnopf
                            aktiv={tool === "highlight"}
                            icon={PdfIcons.markieren}
                            onClick={() => selectTool("highlight")}
                            label="Markieren"
                            title={bild ? "Stelle mit dem Leuchtstift hervorheben" : "Textstelle hervorheben"}
                        />
                        <WerkzeugKnopf
                            aktiv={tool === "streichen"}
                            icon={PdfIcons.durchstreichen}
                            // Eine schon gewählte Textauswahl darf der Klick nicht aufheben — sie wird sofort gestrichen.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectTool("streichen")}
                            label="Durchstreichen"
                            title={
                                bild
                                    ? "Linie über eine Stelle ziehen — sie wird durchgestrichen"
                                    : "Text auswählen — er wird durchgestrichen und bleibt lesbar; auf Scans eine Linie ziehen"
                            }
                            disabled={!ready}
                        />
                        <WerkzeugKnopf
                            aktiv={textFeldOffen}
                            icon={PdfIcons.kopieren}
                            onClick={() => setTextFeldOffen((o) => !o)}
                            label="Text kopieren"
                            title="Erkannten Text des Dokuments anzeigen und kopieren — auch bei Scans"
                        />
                        {kommentare && (
                            <WerkzeugKnopf
                                aktiv={tool === "kommentar"}
                                icon={PdfIcons.kommentar}
                                // Eine schon gewählte Textauswahl darf der Klick nicht aufheben.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectTool("kommentar")}
                                label="Kommentar"
                                title="Text auswählen, Bereich aufziehen oder auf eine Stelle klicken — der Kommentar bleibt in KMVS, nicht im PDF"
                                disabled={!ready}
                            />
                        )}
                    </WerkzeugGruppe>
                    {tool === "streichen" && (
                        <WerkzeugGruppe titel="Strichfarbe">
                            <div className="pdfx-farben">
                                {(Object.keys(STREICH_FARBEN) as StreichFarbe[]).map((f) => (
                                    <button
                                        key={f}
                                        type="button"
                                        className="pdfx-farbe"
                                        aria-pressed={streichFarbe === f}
                                        aria-label={`Strichfarbe ${STREICH_FARBEN[f].name}`}
                                        title={STREICH_FARBEN[f].name}
                                        style={{ background: STREICH_FARBEN[f].css }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => setStreichFarbe(f)}
                                    />
                                ))}
                            </div>
                        </WerkzeugGruppe>
                    )}
                    <WerkzeugGruppe titel="Zoom">
                        <WerkzeugKnopf icon={PdfIcons.zoomMinus} onClick={() => zoom(1 / 1.2)} label="Verkleinern" />
                        <WerkzeugKnopf icon={PdfIcons.zoomPlus} onClick={() => zoom(1.2)} label="Vergrößern" />
                        <WerkzeugKnopf icon={PdfIcons.zoomAnpassen} onClick={zoomAnpassen} label="Anpassen" title="Auf Seitenbreite" />
                    </WerkzeugGruppe>
                </>
            ),
        },
        {
            key: "einfuegen",
            label: "Einfügen",
            inhalt: (
                <WerkzeugGruppe titel="Einfügen">
                    <BausteinMenue
                        disabled={!ready}
                        aktiv={platzieren && platzieren.art !== "bild" ? platzieren.art : null}
                        onWaehlen={(b) => void bausteinWaehlen(b)}
                        onFehler={setError}
                    />
                    <WerkzeugKnopf
                        icon={PdfIcons.bild}
                        onClick={() => setHinweis("Bild einfügen: Bild kopieren und mit Strg+V auf der Seite einsetzen.")}
                        label="Bild"
                        title="Ein kopiertes Bild mit Strg+V einsetzen"
                        disabled={!ready}
                    />
                    {/* Ein Bild hat keine Seiten — einfügen ließe sich nur in ein PDF. */}
                    {!bild && (
                        <WerkzeugKnopf
                            icon={PdfIcons.seiten}
                            onClick={() => void seitenOeffnen()}
                            label="Seiten einfügen"
                            title="Seiten aus anderen Dokumenten oder Dateien einfügen"
                            disabled={!ready || saving || seitenLaden}
                        />
                    )}
                </WerkzeugGruppe>
            ),
        },
        {
            key: "zeichnen",
            label: "Zeichnen",
            inhalt: (
                <WerkzeugGruppe titel="Zeichnen">
                    <WerkzeugKnopf
                        aktiv={tool === "ink"}
                        icon={PdfIcons.zeichnen}
                        onClick={() => selectTool("ink")}
                        label="Zeichnen"
                        title="Freihand zeichnen"
                    />
                </WerkzeugGruppe>
            ),
        },
        {
            key: "bearbeiten",
            label: "Bearbeiten",
            inhalt: (
                <WerkzeugGruppe titel="Bearbeiten">
                    {/* Ein Bild hat keine Textzeilen, die sich ändern ließen. */}
                    {!bild && (
                        <WerkzeugKnopf
                            aktiv={tool === "text"}
                            icon={PdfIcons.textBearbeiten}
                            onClick={() => selectTool("text")}
                            label="Text bearbeiten"
                            title="Vorhandenen Text zeilenweise ändern — Klick auf eine Zeile"
                        />
                    )}
                    <WerkzeugKnopf
                        aktiv={tool === "ausschneiden"}
                        icon={PdfIcons.ausschneiden}
                        onClick={() => selectTool("ausschneiden")}
                        label="Ausschneiden"
                        title={
                            bild
                                ? "Bereich aufziehen, dann Ausschneiden oder Entfernen wählen — der Ausschnitt lässt sich verschieben"
                                : "Bereich aufziehen, dann Ausschneiden oder Entfernen wählen — ausgeschnitten wird als Bild verschoben; Text markieren und Strg+X schneidet direkt aus"
                        }
                    />
                    <WerkzeugKnopf
                        aktiv={textFeldOffen}
                        icon={PdfIcons.kopieren}
                        onClick={() => setTextFeldOffen((o) => !o)}
                        label="Text kopieren"
                        title="Erkannten Text des Dokuments anzeigen und kopieren — auch bei Scans"
                    />
                    <WerkzeugKnopf
                        aktiv={tool === "entfernen"}
                        icon={PdfIcons.entfernen}
                        onClick={() => selectTool("entfernen")}
                        label="Entfernen"
                        title={
                            bild
                                ? "Bereich aufziehen — wird beim Speichern aus dieser Fassung entfernt"
                                : "Text markieren oder Bereich aufziehen — wird beim Speichern aus dieser Fassung entfernt"
                        }
                    />
                    <WerkzeugKnopf
                        aktiv={tool === "schwaerzen"}
                        icon={PdfIcons.schwaerzen}
                        onClick={() => selectTool("schwaerzen")}
                        label="Schwärzen"
                        title={
                            bild
                                ? "Bereich aufziehen — wird beim Speichern schwarz, darunter bleibt nichts"
                                : "Text markieren oder Bereich aufziehen — wird beim Speichern echt entfernt und mit einem schwarzen Balken überdeckt"
                        }
                    />
                </WerkzeugGruppe>
            ),
        },
        {
            key: "seiten",
            label: bild ? "Bild" : "Seiten",
            inhalt: (
                <WerkzeugGruppe titel={bild ? "Bild" : "Seiten"}>
                    {!bild && (
                        <WerkzeugKnopf
                            icon={PdfIcons.seiten}
                            onClick={() => void seitenOeffnen()}
                            label="Seiten"
                            title="Seiten sortieren, drehen, löschen oder aus anderen Dokumenten einfügen"
                            disabled={!ready || saving || seitenLaden}
                        />
                    )}
                    <WerkzeugKnopf
                        aktiv={drehung !== 0}
                        icon={PdfIcons.drehen}
                        onClick={drehen}
                        label="Drehen"
                        title={
                            bild
                                ? "Das Bild um 90° im Uhrzeigersinn drehen — wird beim Speichern übernommen"
                                : "Alle Seiten um 90° im Uhrzeigersinn drehen — wird beim Speichern übernommen"
                        }
                        disabled={!ready}
                    />
                </WerkzeugGruppe>
            ),
        },
        {
            key: "ansicht",
            label: "Ansicht",
            inhalt: (
                <WerkzeugGruppe titel="Ansicht">
                    <WerkzeugKnopf icon={PdfIcons.zoomMinus} onClick={() => zoom(1 / 1.2)} label="Verkleinern" />
                    <WerkzeugKnopf icon={PdfIcons.zoomPlus} onClick={() => zoom(1.2)} label="Vergrößern" />
                    <WerkzeugKnopf icon={PdfIcons.zoomAnpassen} onClick={zoomAnpassen} label="Seitenbreite" title="Auf Seitenbreite" />
                    {kommentare && (
                        <WerkzeugKnopf
                            aktiv={kommentare.sichtbar}
                            icon={kommentare.sichtbar ? PdfIcons.kommentareAn : PdfIcons.kommentareAus}
                            onClick={() => {
                                if (kommentare.sichtbar) kommentare.setOffen(null);
                                kommentare.setSichtbar(!kommentare.sichtbar);
                            }}
                            label={`Kommentare (${kommentare.anzahlOffen})`}
                            title={kommentare.sichtbar ? "Kommentare ausblenden" : "Kommentare einblenden"}
                        />
                    )}
                    {kommentare && <KommentareOhneStelle kommentare={kommentare} />}
                </WerkzeugGruppe>
            ),
        },
    ];

    return (
        <div className="pdfx pdfx-root">
            {/* Der Organisator bringt seine eigene Kopfzeile mit und ersetzt die Leiste. */}
            {modus === "seiten" && organisatorBytes && (
                <SeitenOrganisator
                    docId={docId}
                    bytes={organisatorBytes}
                    offeneDrehung={drehung as Drehung}
                    onUebernehmen={seitenUebernehmen}
                    onAbbrechen={seitenAbbrechen}
                />
            )}
            <Werkzeugleiste
                verborgen={modus === "seiten"}
                hinweis={hinweisZeile}
                reiter={reiterListe}
                aktiv={reiter}
                onReiter={setReiter}
                schnell={
                    <>
                        <SchnellKnopf
                            icon={PdfIcons.rueckgaengig}
                            onClick={rueckgaengig}
                            disabled={!kannRueckgaengig || saving}
                            title="Rückgängig (Strg+Z)"
                            aria-label="Rückgängig"
                        />
                        <SchnellKnopf
                            icon={PdfIcons.wiederholen}
                            onClick={wiederholen}
                            disabled={!kannWiederholen || saving}
                            title="Wiederholen (Strg+Y)"
                            aria-label="Wiederholen"
                        />
                        <span className="pdfx-trenner" aria-hidden />
                        <span className="pdfx-datei" title={fileName}>
                            {fileName}
                        </span>
                    </>
                }
                rechts={
                    <>
                        <SchnellKnopf icon={PdfIcons.schliessen} onClick={() => void cancel()} disabled={saving}>
                            Schließen
                        </SchnellKnopf>
                        <SchnellKnopf
                            primaer
                            icon={PdfIcons.speichern}
                            onClick={() => setDialogOffen(true)}
                            disabled={saving || !ready || seitenLaden}
                        >
                            {saving ? "Wird gespeichert…" : "Speichern"}
                        </SchnellKnopf>
                    </>
                }
            />

            {/* Im Seitenmodus bleibt der PDF.js-Container gemountet, nur versteckt —
                erst „Übernehmen“ lädt ihn neu. */}
            <div className="pdfx-stage" style={modus === "seiten" ? { display: "none" } : undefined}>
                {/* PDF.js erwartet genau diese Verschachtelung: scrollender
                    Container mit einem `.pdfViewer`-Kind, das es selbst füllt. */}
                <div ref={containerRef} className="pdfx-container">
                    <div className="pdfViewer" />
                </div>
                <PlatzierEbene
                    auftrag={platzieren}
                    viewer={viewerVon}
                    container={containerRef.current}
                    onBreite={platzierBreite}
                    onSetzen={platzierenSetzen}
                    onAbbruch={platzierenAbbrechen}
                />
                <EntfernenEbene
                    viewer={ready ? viewerRef.current : null}
                    container={containerRef.current}
                    aktiv={tool === "entfernen" || tool === "schwaerzen"}
                    schwaerzen={tool === "schwaerzen"}
                    stand={bearbeitung}
                    dispatch={aendern}
                    onHinweis={setHinweis}
                />
                <StreichEbene
                    viewer={ready ? viewerRef.current : null}
                    container={containerRef.current}
                    aktiv={tool === "streichen"}
                    eintraege={bearbeitung.streichen}
                    farbe={streichFarbe}
                    dispatch={aendern}
                />
                <AusschnittEbene
                    viewer={ready ? viewerRef.current : null}
                    container={containerRef.current}
                    aktiv={tool === "ausschneiden"}
                    stand={bearbeitung}
                    dispatch={aendern}
                    onHinweis={setHinweis}
                    aufnehmen={ausschnittAufnehmen}
                />
                <TextEbene
                    viewer={ready ? viewerRef.current : null}
                    container={containerRef.current}
                    pdf={ready ? pdfRef.current : null}
                    aktiv={tool === "text"}
                    ersetzen={bearbeitung.ersetzen}
                    dispatch={aendern}
                    onHinweis={setHinweis}
                />
                {kommentare && (
                    <KommentarEbene
                        geometrie={kommentarGeometrie}
                        container={containerRef.current}
                        kommentare={kommentare}
                        werkzeugAktiv={tool === "kommentar"}
                        darfSchreiben
                        onEntwurf={kommentarEntwurf}
                        seitenZuordnung={kommentarSeiten}
                        onHinweis={setHinweis}
                    />
                )}
                {textFeldOffen && <ErkannterTextFeld docId={docId} onSchliessen={() => setTextFeldOffen(false)} />}
                {!ready && !error && (
                    <div className="pdfx-status" role="status">
                        {bild ? "Bild wird geladen…" : "PDF wird geladen…"}
                    </div>
                )}
            </div>

            <SpeichernPopup
                offen={dialogOffen}
                name={name}
                onName={setName}
                kommentar={kommentar}
                onKommentar={setKommentar}
                saving={saving}
                onSpeichern={() => void save()}
                onSchliessen={() => !saving && setDialogOffen(false)}
                anzahlEntfernen={anzahlEntfernen}
                anzahlVerschoben={anzahlVerschoben}
                anzahlZeilen={anzahlZeilen}
                anzahlGeschwaerzt={geschwaerzt}
                fassung={
                    fassung ? { versionNo: fassung.versionNo, ziel: fassungZiel, onZiel: fassungZielWaehlen } : undefined
                }
                bild={
                    bild
                        ? {
                              ziel: bildZiel,
                              onZiel: bildZielWaehlen,
                              formatName: bild.ziel.anzeige,
                              formatWechsel: bild.art === "gif" || bild.art === "bmp",
                              unveraendert: !hatAenderungen,
                          }
                        : undefined
                }
            />
        </div>
    );
};
